// frontend/src/pages/AdminDashboard/AdminDashboard.js
import React, { useState, useEffect } from 'react';
// Tailwind redesign: se elimina la hoja de estilos específica y se usan utilidades.
import { 
  FaUsers, 
  FaProjectDiagram, 
  FaTasks, 
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaChartLine,
  FaDollarSign
} from 'react-icons/fa';
import { fetchAuth } from '../../utils/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsuarios: 0,
    usuariosActivos: 0,
    usuariosInactivos: 0,
    totalProyectos: 0,
    totalTareas: 0,
    tareasCompletadas: 0,
    tareasEnProgreso: 0,
    tareasPendientes: 0,
    pagosPendientes: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEstadisticas = async () => {
      try {
        setLoading(true);
        setError(null);

        // Llamar al endpoint de estadísticas usando wrapper con auth y mejor manejo de errores
        const data = await fetchAuth('/api/admin/estadisticas/');
        console.log('✅ Estadísticas cargadas:', data);

        setStats(data);
        setLoading(false);
      } catch (error) {
        console.error('❌ Error al cargar estadísticas:', error);
        // Mostrar mensaje claro si es 403 o si faltan permisos/configuración
        if (error.status === 403) {
          setError('Acceso denegado. Inicia sesión con una cuenta de Administrador.');
        } else if (error.status === 500) {
          setError('Error del servidor al obtener estadísticas. Verifica la configuración de Supabase (SERVICE ROLE KEY).');
        } else {
          setError(error.message || 'No se pudieron cargar las estadísticas');
        }
        setLoading(false);
      }
    };

    fetchEstadisticas();
  }, []);

  if (loading) {
    return (
      <div className="flex-1 p-6 lg:p-10 bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-lg overflow-y-auto max-h-[calc(100vh-3rem)] flex items-center justify-center">
        <div className="relative">
          {/* Backdrop blur overlay */}
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm rounded-2xl animate-[fadeIn_0.3s_ease]" />
          
          {/* Loading card */}
          <div className="relative bg-white rounded-2xl border border-slate-200 shadow-xl p-8 animate-[slideUp_0.4s_cubic-bezier(0.4,0,0.2,1)]">
            <div className="flex flex-col items-center gap-5">
              {/* Spinner with gradient */}
              <div className="relative">
                <div className="h-16 w-16 rounded-full bg-gradient-to-tr from-slate-200 to-slate-100 animate-pulse" />
                <div className="absolute inset-0 h-16 w-16 rounded-full border-4 border-transparent border-t-slate-600 animate-spin" aria-label="Cargando" />
              </div>
              
              {/* Loading text */}
              <div className="text-center space-y-2">
                <p className="text-base font-semibold text-slate-800">Cargando estadísticas</p>
                <p className="text-sm text-slate-500">Un momento por favor…</p>
              </div>
              
              {/* Progress dots */}
              <div className="flex gap-2">
                <div className="h-2 w-2 rounded-full bg-slate-400 animate-[bounce_1s_infinite_0ms]" />
                <div className="h-2 w-2 rounded-full bg-slate-400 animate-[bounce_1s_infinite_200ms]" />
                <div className="h-2 w-2 rounded-full bg-slate-400 animate-[bounce_1s_infinite_400ms]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-6 lg:p-10 bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-lg overflow-y-auto max-h-[calc(100vh-3rem)] flex items-center justify-center">
        {/* Error modal overlay */}
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 animate-[fadeIn_0.2s_ease]">
          <div className="w-full max-w-lg mx-4 bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden animate-[slideUp_0.3s_cubic-bezier(0.4,0,0.2,1)]">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 px-6 py-5 border-b border-red-100">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-red-500 to-orange-600 text-white shadow-lg animate-[wiggle_0.5s_ease]">
                  <FaExclamationTriangle className="text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold text-slate-900 mb-1">Ocurrió un problema</h2>
                  <p className="text-sm text-slate-600">No se pudieron cargar las estadísticas</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="px-6 py-5">
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
                <p className="text-sm text-red-800 leading-relaxed">{error}</p>
              </div>
            </div>
            
            {/* Actions */}
            <div className="bg-slate-50 px-6 py-4 flex gap-3 justify-end border-t border-slate-200">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-slate-700 to-slate-800 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg hover:from-slate-800 hover:to-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 transition-all duration-200 hover:scale-[1.02]"
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tasksProgress = stats.totalTareas > 0 
    ? Math.round((stats.tareasCompletadas / stats.totalTareas) * 100) 
    : 0;
  const usersProgress = stats.totalUsuarios > 0
    ? Math.round((stats.usuariosActivos / stats.totalUsuarios) * 100)
    : 0;

  const Donut = ({ value = 0, size = 64, color = '#10b981', label }) => {
    const pct = Math.max(0, Math.min(100, value));
    const style = {
      width: size,
      height: size,
      background: `conic-gradient(${color} ${pct}%, #e2e8f0 0)`
    };
    return (
      <div className="group relative" aria-label={label} role="img">
        <div className="relative rounded-full shadow-inner" style={style}>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-semibold text-slate-700">{pct}%</span>
          </div>
          <div className="absolute inset-[18%] rounded-full bg-white" />
        </div>
        <div className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 rounded-md bg-slate-900 text-white text-[10px] px-2 py-1 opacity-0 group-hover:opacity-100 shadow">
          {label}
        </div>
        <span className="sr-only">{pct}%</span>
      </div>
    );
  };

  const MiniBars = ({ items = [] }) => {
    const total = items.reduce((s, i) => s + (i.value || 0), 0) || 1;
    return (
      <div className="group flex h-2 w-full overflow-hidden rounded bg-slate-100" aria-label="Distribución" role="img">
        {items.map((i, idx) => (
          <div
            key={idx}
            className="relative h-full first:rounded-l last:rounded-r"
            style={{ width: `${(i.value / total) * 100}%`, background: i.color }}
            title={`${i.label}: ${i.value}`}
          >
            <div className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 text-white text-[10px] px-2 py-1 opacity-0 group-hover:opacity-100 shadow">
              {i.label}: {i.value}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const TrendBadge = ({ value = 0, positiveColor = 'text-emerald-700 bg-emerald-50', negativeColor = 'text-red-700 bg-red-50' }) => {
    const isPositive = value >= 0;
    const sign = isPositive ? '+' : '';
    const cls = `inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ${isPositive ? positiveColor : negativeColor}`;
    return (
      <div className={cls}>
        <FaChartLine className={isPositive ? 'text-emerald-600' : 'text-red-600'} />
        <span>{sign}{Math.abs(value)}% vs mes anterior</span>
      </div>
    );
  };

  return (
    <div className="flex-1 p-6 lg:p-10 bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-lg overflow-y-auto max-h-[calc(100vh-3rem)]">
      <header className="mb-10">
        <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-slate-900 to-indigo-600 bg-clip-text text-transparent">
          Panel de Administración
        </h1>
        <p className="mt-2 text-sm text-slate-600 max-w-prose">
          Vista general del sistema y estadísticas en tiempo real
        </p>
      </header>

      {/* Estadísticas Principales */}
      <section className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4 mb-12">
        {/* Usuarios */}
        <div className="group relative rounded-xl border border-slate-200 bg-white/80 backdrop-blur p-5 shadow-sm hover:shadow-xl transition duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">USUARIOS DEL SISTEMA</h3>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-md group-hover:scale-105 group-hover:rotate-3 transition">
              <FaUsers />
            </div>
          </div>
          <div className="space-y-6">
            <div className="flex items-baseline gap-3">
              <h2 className="text-4xl font-extrabold tracking-tight text-slate-800">{stats.totalUsuarios}</h2>
              <span className="text-sm font-medium text-slate-500">Registrados</span>
            </div>
            <TrendBadge value={5} />
            <div className="flex items-center gap-4">
              <Donut value={usersProgress} size={64} color="#6366f1" label="Usuarios activos" />
              <div className="flex flex-col">
                <span className="text-lg font-bold text-slate-800">{usersProgress}%</span>
                <span className="text-xs uppercase tracking-wide text-slate-500">ACTIVOS</span>
              </div>
            </div>
            <MiniBars
              items={[
                { value: stats.usuariosActivos, color: '#6366f1', label: 'Activos' },
                { value: stats.usuariosInactivos, color: '#cbd5e1', label: 'Inactivos' }
              ]}
            />
            <div className="mt-3 h-1.5 w-full rounded bg-slate-200">
              <div className="h-full rounded bg-indigo-500" style={{ width: `${usersProgress}%` }} />
            </div>
          </div>
        </div>

        {/* Proyectos */}
        <div className="group relative rounded-xl border border-slate-200 bg-white/80 backdrop-blur p-5 shadow-sm hover:shadow-xl transition duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">PROYECTOS ACTIVOS</h3>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-violet-600 text-white shadow-md group-hover:scale-105 group-hover:rotate-3 transition">
              <FaProjectDiagram />
            </div>
          </div>
          <div className="space-y-6">
            <div className="flex items-baseline gap-3">
              <h2 className="text-4xl font-extrabold tracking-tight text-slate-800">{stats.totalProyectos}</h2>
              <span className="text-sm font-medium text-slate-500">En ejecución</span>
            </div>
            <TrendBadge value={20} positiveColor="text-violet-700 bg-violet-50" negativeColor="text-violet-700 bg-violet-50" />
          </div>
        </div>

        {/* Tareas */}
        <div className="group relative rounded-xl border border-slate-200 bg-white/80 backdrop-blur p-5 shadow-sm hover:shadow-xl transition duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">GESTIÓN DE TAREAS</h3>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md group-hover:scale-105 group-hover:rotate-3 transition">
              <FaTasks />
            </div>
          </div>
          <div className="space-y-6">
            <div className="flex items-baseline gap-3">
              <h2 className="text-4xl font-extrabold tracking-tight text-slate-800">{stats.totalTareas}</h2>
              <span className="text-sm font-medium text-slate-500">Tareas totales</span>
            </div>
            <TrendBadge value={12} positiveColor="text-emerald-700 bg-emerald-50" negativeColor="text-emerald-700 bg-emerald-50" />
            <div className="flex items-center gap-4">
              <Donut value={tasksProgress} size={64} color="#10b981" label="Avance de tareas" />
              <div className="flex flex-col">
                <span className="text-lg font-bold text-slate-800">{tasksProgress}%</span>
                <span className="text-xs uppercase tracking-wide text-slate-500">COMPLETADO</span>
              </div>
            </div>
            <MiniBars
              items={[
                { value: stats.tareasCompletadas, color: '#10b981', label: 'Completadas' },
                { value: stats.tareasEnProgreso, color: '#8b5cf6', label: 'En Progreso' },
                { value: stats.tareasPendientes, color: '#f59e0b', label: 'Pendientes' }
              ]}
            />
          </div>
        </div>

        {/* Pagos Pendientes */}
        <div className="group relative rounded-xl border border-slate-200 bg-white/80 backdrop-blur p-5 shadow-sm hover:shadow-xl transition duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">PAGOS PENDIENTES</h3>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-md group-hover:scale-105 group-hover:rotate-3 transition">
              <FaDollarSign />
            </div>
          </div>
          <div className="space-y-6">
            <div className="flex items-baseline gap-3">
              <h2 className="text-4xl font-extrabold tracking-tight text-slate-800">{stats.pagosPendientes}</h2>
              <span className="text-sm font-medium text-slate-500">Por procesar</span>
            </div>
            <TrendBadge value={-10} positiveColor="text-amber-700 bg-amber-50" negativeColor="text-amber-700 bg-amber-50" />
            <div className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-amber-700 text-sm font-medium">
              <FaExclamationTriangle className="text-amber-500" />
              <span>Requiere revisión</span>
            </div>
          </div>
        </div>
      </section>

      {/* Resumen de Tareas */}
      <section className="mb-12">
        <h2 className="text-xl font-bold tracking-tight text-slate-800 mb-6">Distribución de Tareas</h2>
        <div className="grid gap-5 sm:grid-cols-3">
          <div className="group flex items-center gap-4 rounded-xl border border-emerald-200 bg-white/70 backdrop-blur px-5 py-4 shadow-sm hover:shadow-lg transition">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700 shadow">
              <FaCheckCircle className="text-2xl" />
            </div>
            <div className="flex flex-col">
              <span className="text-3xl font-extrabold tracking-tight text-slate-800">{stats.tareasCompletadas}</span>
              <span className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Completadas</span>
            </div>
          </div>
          <div className="group flex items-center gap-4 rounded-xl border border-indigo-200 bg-white/70 backdrop-blur px-5 py-4 shadow-sm hover:shadow-lg transition">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-100 to-indigo-200 text-indigo-700 shadow">
              <FaClock className="text-2xl" />
            </div>
            <div className="flex flex-col">
              <span className="text-3xl font-extrabold tracking-tight text-slate-800">{stats.tareasEnProgreso}</span>
              <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600">En Progreso</span>
            </div>
          </div>
          <div className="group flex items-center gap-4 rounded-xl border border-amber-200 bg-white/70 backdrop-blur px-5 py-4 shadow-sm hover:shadow-lg transition">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 text-amber-700 shadow">
              <FaExclamationTriangle className="text-2xl" />
            </div>
            <div className="flex flex-col">
              <span className="text-3xl font-extrabold tracking-tight text-slate-800">{stats.tareasPendientes}</span>
              <span className="text-xs font-semibold uppercase tracking-wide text-amber-600">Pendientes</span>
            </div>
          </div>
        </div>
      </section>

      {/* Hint */}
      <div className="rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 to-sky-100 px-6 py-4 text-sky-900 shadow-sm">
        <p className="text-sm leading-relaxed">
          💡 <strong className="font-semibold">Consejo:</strong> Utiliza el menú lateral para acceder a las diferentes secciones de administración
        </p>
      </div>
    </div>
  );
}